import React, { useState, useEffect } from 'react';
import api from '../api';

export default function KPI() {
  const [data, setData] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [clientFilter, setClientFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', client_id: '', metric_name: '', target: '', unit: 'Number', category: 'Reach & Impressions' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const UNITS = ['Number', '%', '₹', 'ROAS'];
  const CATEGORIES = ['Reach & Impressions', 'Engagement', 'Conversions', 'Revenue', 'Satisfaction'];
  const COMMON_METRICS = ['Reach', 'Impressions', 'Likes', 'Comments', 'Shares', 'Saves', 'Reactions', 'Conversions', 'Clicks', 'Views'];

  useEffect(() => { loadClients(); }, []);
  useEffect(() => { loadKPIs(); }, [period, clientFilter]);

  const loadClients = async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data.data);
    } catch {}
  };

  const loadKPIs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (clientFilter) params.append('client_id', clientFilter);
      const res = await api.get(`/kpis/compute?${params}`);
      setData(res.data.data);
    } catch {} finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.name || !form.client_id || !form.metric_name || !form.target) {
      setFormError('All fields are required'); return;
    }
    setSaving(true);
    try {
      await api.post('/kpis', { ...form, target: parseFloat(form.target) });
      setShowForm(false);
      setForm({ name: '', client_id: '', metric_name: '', target: '', unit: 'Number', category: 'Reach & Impressions' });
      loadKPIs();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create KPI.');
    } finally { setSaving(false); }
  };

  const deleteKPI = async (id) => {
    if (!window.confirm('Delete this KPI target?')) return;
    await api.delete(`/kpis/${id}`);
    loadKPIs();
  };

  const statusColor = (status) => {
    switch(status) {
      case 'On Track': return '#10b981';
      case 'Near': return '#f59e0b';
      case 'Off Track': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.pageTitle}>📈 KPI Tracker</div>
          <div style={styles.pageSubtitle}>Actuals computed live from monitoring observations</div>
        </div>
        <div style={styles.controls}>
          <select style={styles.select} value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="month" style={styles.monthInput} value={period} onChange={e => setPeriod(e.target.value)} />
          <button style={styles.btnSecondary} onClick={() => setPeriod(new Date().toISOString().slice(0, 7))}>This Month</button>
          <button style={styles.btnPrimary} onClick={() => setShowForm(true)}>+ Add KPI</button>
        </div>
      </div>

      {/* KPI Cards */}
      {loading ? (
        <div style={styles.loading}>Computing actuals...</div>
      ) : data.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>📈</div>
          <div style={styles.emptyTitle}>No KPI targets yet</div>
          <div style={styles.emptySub}>Click "+ Add KPI" to set your first target.</div>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={styles.cardGrid}>
            {data.map(k => {
              const pct = k.pct_achieved || 0;
              const color = statusColor(k.status);
              return (
                <div key={k.id} style={{...styles.kpiCard, borderColor: color + '44'}}>
                  <div style={styles.kpiCardHeader}>
                    <div style={styles.kpiName}>{k.name}</div>
                    <button style={styles.deleteBtn} onClick={() => deleteKPI(k.id)}>✕</button>
                  </div>
                  <div style={styles.kpiClient}>{k.client?.name} · {k.metric_name}</div>

                  <div style={styles.kpiNumbers}>
                    <div>
                      <div style={{...styles.kpiActual, color}}>
                        {k.found_data ? k.actual.toLocaleString('en-IN') : '—'}
                        {k.unit !== 'Number' ? k.unit : ''}
                      </div>
                      <div style={styles.kpiLabel}>Actual</div>
                    </div>
                    <div style={styles.kpiDivider} />
                    <div>
                      <div style={styles.kpiTarget}>
                        {Number(k.target).toLocaleString('en-IN')}
                        {k.unit !== 'Number' ? k.unit : ''}
                      </div>
                      <div style={styles.kpiLabel}>Target</div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div style={styles.progressBg}>
                    <div style={{...styles.progressFill, width: `${Math.min(100, pct)}%`, background: color}} />
                  </div>
                  <div style={styles.kpiFooter}>
                    <span style={{color}}>{k.found_data ? `${pct}% achieved` : 'No data this period'}</span>
                    <span style={{...styles.statusBadge, background: color + '22', color, border: `1px solid ${color}44`}}>
                      {k.status}
                    </span>
                  </div>

                  {/* Per-platform breakdown */}
                  {k.by_platform && Object.keys(k.by_platform).length > 0 && (
                    <div style={styles.breakdown}>
                      {Object.entries(k.by_platform).map(([p, v]) => (
                        <span key={p} style={styles.breakdownItem}>
                          {p}: <strong>{v.toLocaleString('en-IN')}</strong>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Table */}
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>KPI</th>
                  <th style={styles.th}>Client</th>
                  <th style={styles.th}>Metric</th>
                  <th style={styles.th}>Target</th>
                  <th style={styles.th}>Actual</th>
                  <th style={styles.th}>% Achieved</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.map(k => {
                  const color = statusColor(k.status);
                  return (
                    <tr key={k.id} style={styles.tr}>
                      <td style={styles.td}><span style={styles.kpiNameTd}>{k.name}</span></td>
                      <td style={styles.td}><span style={styles.tdText}>{k.client?.name}</span></td>
                      <td style={styles.td}><span style={styles.tdMuted}>{k.metric_name}</span></td>
                      <td style={styles.td}><span style={styles.tdMono}>{Number(k.target).toLocaleString('en-IN')}</span></td>
                      <td style={styles.td}><span style={{...styles.tdMono, color}}>{k.found_data ? k.actual.toLocaleString('en-IN') : '—'}</span></td>
                      <td style={styles.td}>
                        <div style={styles.miniProgress}>
                          <div style={styles.miniProgressBg}>
                            <div style={{...styles.miniProgressFill, width: `${Math.min(100, k.pct_achieved || 0)}%`, background: color}} />
                          </div>
                          <span style={{color, fontSize: '12px'}}>{k.found_data ? `${k.pct_achieved}%` : '—'}</span>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={{...styles.statusBadge, background: color + '22', color, border: `1px solid ${color}44`}}>{k.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Add KPI Modal */}
      {showForm && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>+ Add KPI Target</div>
              <button style={styles.closeBtn} onClick={() => setShowForm(false)}>✕</button>
            </div>
            {formError && <div style={styles.formError}>{formError}</div>}
            <form onSubmit={handleSubmit}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>KPI Name *</label>
                  <input style={styles.input} value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Monthly Reach" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Client *</label>
                  <select style={styles.input} value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})}>
                    <option value="">Select client...</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Metric to Track *</label>
                <div style={styles.metricRow}>
                  <select style={{...styles.input, flex: 1}} value={form.metric_name} onChange={e => setForm({...form, metric_name: e.target.value})}>
                    <option value="">Select or type below...</option>
                    {COMMON_METRICS.map(m => <option key={m}>{m}</option>)}
                  </select>
                  <input style={{...styles.input, flex: 1}} placeholder="Or type custom metric" value={form.metric_name} onChange={e => setForm({...form, metric_name: e.target.value})} />
                </div>
                <div style={styles.hint}>Must match the metric name used in Monitoring observations exactly.</div>
              </div>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Target Value *</label>
                  <input style={styles.input} type="number" value={form.target} onChange={e => setForm({...form, target: e.target.value})} placeholder="50000" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Unit</label>
                  <select style={styles.input} value={form.unit} onChange={e => setForm({...form, unit: e.target.value})}>
                    {UNITS.map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Category</label>
                <select style={styles.input} value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div style={styles.modalFooter}>
                <button type="button" style={styles.btnSecondary} onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" style={styles.btnPrimary} disabled={saving}>{saving ? 'Saving...' : 'Add KPI Target'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '24px', maxWidth: '1200px', margin: '0 auto' },
  loading: { padding: '40px', textAlign: 'center', color: '#9ca3af' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  pageTitle: { color: '#fff', fontSize: '22px', fontWeight: '700' },
  pageSubtitle: { color: '#6b7280', fontSize: '13px', marginTop: '2px' },
  controls: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  select: { padding: '8px 12px', background: '#1a1d27', border: '1px solid #2a2d3e', borderRadius: '8px', color: '#e5e7eb', fontSize: '13px' },
  monthInput: { padding: '8px 12px', background: '#1a1d27', border: '1px solid #2a2d3e', borderRadius: '8px', color: '#e5e7eb', fontSize: '13px' },
  btnPrimary: { padding: '8px 16px', background: 'linear-gradient(135deg, #6c58f0, #4f8ef7)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  btnSecondary: { padding: '8px 16px', background: 'transparent', border: '1px solid #2a2d3e', borderRadius: '8px', color: '#9ca3af', fontSize: '13px', cursor: 'pointer' },
  empty: { textAlign: 'center', padding: '60px 20px' },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { color: '#fff', fontSize: '18px', fontWeight: '600', marginBottom: '8px' },
  emptySub: { color: '#6b7280', fontSize: '14px' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' },
  kpiCard: { background: '#1a1d27', border: '1px solid', borderRadius: '12px', padding: '18px' },
  kpiCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' },
  kpiName: { color: '#fff', fontSize: '14px', fontWeight: '600' },
  deleteBtn: { background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '14px' },
  kpiClient: { color: '#6b7280', fontSize: '12px', marginBottom: '14px' },
  kpiNumbers: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' },
  kpiActual: { fontSize: '24px', fontWeight: '700' },
  kpiTarget: { fontSize: '18px', fontWeight: '600', color: '#9ca3af' },
  kpiLabel: { color: '#6b7280', fontSize: '11px', marginTop: '2px' },
  kpiDivider: { width: '1px', height: '40px', background: '#2a2d3e' },
  progressBg: { height: '6px', background: '#2a2d3e', borderRadius: '3px', marginBottom: '8px' },
  progressFill: { height: '100%', borderRadius: '3px', transition: 'width 0.3s' },
  kpiFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', marginBottom: '8px' },
  statusBadge: { padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  breakdown: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #2a2d3e' },
  breakdownItem: { color: '#6b7280', fontSize: '11px' },
  tableWrap: { background: '#1a1d27', border: '1px solid #2a2d3e', borderRadius: '12px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', color: '#6b7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', borderBottom: '1px solid #2a2d3e' },
  tr: { borderBottom: '1px solid #2a2d3e' },
  td: { padding: '12px 16px', verticalAlign: 'middle' },
  kpiNameTd: { color: '#e5e7eb', fontSize: '13px', fontWeight: '500' },
  tdText: { color: '#9ca3af', fontSize: '13px' },
  tdMuted: { color: '#6b7280', fontSize: '13px' },
  tdMono: { fontSize: '13px', fontFamily: 'monospace', fontWeight: '600' },
  miniProgress: { display: 'flex', alignItems: 'center', gap: '8px' },
  miniProgressBg: { height: '6px', background: '#2a2d3e', borderRadius: '3px', width: '80px' },
  miniProgressFill: { height: '100%', borderRadius: '3px' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { background: '#1a1d27', border: '1px solid #2a2d3e', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  modalTitle: { color: '#fff', fontSize: '18px', fontWeight: '700' },
  closeBtn: { background: 'transparent', border: 'none', color: '#6b7280', fontSize: '18px', cursor: 'pointer' },
  formError: { background: '#2d1515', border: '1px solid #ef4444', color: '#ef4444', padding: '10px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', color: '#9ca3af', fontSize: '13px', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', background: '#0f1117', border: '1px solid #2a2d3e', borderRadius: '8px', color: '#fff', fontSize: '14px', boxSizing: 'border-box', outline: 'none' },
  metricRow: { display: 'flex', gap: '8px' },
  hint: { color: '#6b7280', fontSize: '11px', marginTop: '4px' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #2a2d3e' },
};